import {
  Bot,
  ChevronRight,
  Hammer,
  Home,
  LibraryBig,
  Link2,
  MessageSquareText,
  Network,
  Radio,
  Search,
  Settings2,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "~/components/ui/sidebar";
import { type User } from "~/context/AuthContext";
import useIsEcommerce from "~/hooks/useIsEcommerce";
import { useTeamAgency } from "~/hooks/useTeamAccess";
import { apiClient } from "~/lib/api-client";
import { isHiddenForPitch } from "~/lib/pitch";
import { Link, useLocation, useNavigate } from "~/lib/team-router";
import AgencySidebarBrand from "./agencies/AgencySidebarBrand";
import { NavUser } from "./NavUser";
import TeamSwitcher from "./TeamSwitcher";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

const keywordClustersItem = {
  title: "Keyword Clusters",
  path: "/keyword-clusters",
  icon: Network,
};

const zerplyAgentItem = {
  title: "Zerply Agent",
  path: "/chats/home",
  icon: Bot,
};

const libraryItem = {
  title: "Library",
  path: "/library",
  icon: LibraryBig,
};

const aiVisibilityItems = [
  {
    title: "Home",
    path: "/ai-visibility/home",
    icon: Home,
  },
  {
    title: "Prompts",
    path: "/ai-visibility/prompts",
    icon: MessageSquareText,
  },
  {
    title: "Sources",
    path: "/ai-visibility/sources",
    icon: Link2,
  },
  {
    title: "Query Fanouts",
    path: "/ai-visibility/fanouts",
    icon: Search,
  },
  {
    title: "Signals",
    path: "/ai-visibility/signals",
    icon: Radio,
  },
  {
    title: "Shopping",
    path: "/shopping",
    icon: ShoppingBag,
  },
  {
    title: "AI Traffic Analytics",
    path: "/ai-visibility/ai-traffic-analytics",
    icon: Sparkles,
  },
] as const;

interface AppSidebarProps {
  user: User;
  sidebarOpen: boolean;
  conversations: Conversation[];
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
}

export default function AppSidebar({
  user,
  sidebarOpen,
  setConversations,
}: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEcommerce = useIsEcommerce();
  const teamAgency = useTeamAgency();
  const isLoadingConversations = useRef(false);

  const homePath = teamAgency ? "/agency/home" : "/ai-visibility/home";
  const teams = user.teams ?? [];
  const activeTeam = teams.find((team) => team.id == user.team_id) || teams[0];
  const brand = activeTeam?.agency ?? teamAgency;
  const isPitch = Boolean(activeTeam?.is_pitch);

  const visibleAiVisibilityItems = useMemo(() => {
    return aiVisibilityItems.filter(
      (item) =>
        !(isPitch && isHiddenForPitch(item.path)) &&
        (item.path !== "/shopping" || isEcommerce)
    );
  }, [isPitch, isEcommerce]);

  const fetchConversations = useCallback(async () => {
    if (isLoadingConversations.current) return;
    isLoadingConversations.current = true;
    try {
      const response = await apiClient.get("/chats");
      if (response.data.data) {
        setConversations(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      isLoadingConversations.current = false;
    }
  }, [setConversations]);

  useEffect(() => {
    fetchConversations();

    const handleConversationUpdated = () => {
      fetchConversations();
    };

    window.addEventListener("conversationUpdated", handleConversationUpdated);
    return () => {
      window.removeEventListener(
        "conversationUpdated",
        handleConversationUpdated
      );
    };
  }, [user.team_id, fetchConversations]);

  return (
    <Sidebar collapsible="offcanvas" variant="inset">
      <SidebarHeader className="shrink-0">
        <SidebarMenu className="mb-2">
          {brand ? (
            <SidebarMenuItem>
              <AgencySidebarBrand
                agency={brand}
                sidebarOpen={sidebarOpen}
                onClick={() => navigate(homePath)}
                showAgencyLabel={Boolean(teamAgency)}
              />
            </SidebarMenuItem>
          ) : (
            <SidebarMenuItem
              className="cursor-pointer hover:bg-sidebar-accent"
              onClick={() => navigate(homePath)}
            >
              {sidebarOpen ? (
                <img src="/logo-w-logotype.svg" alt="logo" className="w-32" />
              ) : (
                <img src="/logo.svg" alt="logo" className="w-8" />
              )}
            </SidebarMenuItem>
          )}
        </SidebarMenu>

        {teams.length > 0 ? (
          <TeamSwitcher teams={teams} activeTeam={activeTeam} />
        ) : null}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {visibleAiVisibilityItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  className="py-4 px-2 cursor-pointer"
                  tooltip={item.title}
                  isActive={
                    location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`)
                  }
                  onClick={() => navigate(item.path)}
                >
                  <item.icon />
                  <span className="text-sm">{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
            {isPitch ? null : (
              <>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="py-4 px-2 cursor-pointer"
                    tooltip={zerplyAgentItem.title}
                    isActive={location.pathname.startsWith("/chats/")}
                    onClick={() => navigate(zerplyAgentItem.path)}
                  >
                    <zerplyAgentItem.icon />
                    <span className="text-sm">{zerplyAgentItem.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="py-4 px-2 cursor-pointer"
                    tooltip={libraryItem.title}
                    isActive={location.pathname === libraryItem.path}
                    onClick={() => navigate(libraryItem.path)}
                  >
                    <libraryItem.icon />
                    <span className="text-sm">{libraryItem.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <Collapsible
                  asChild
                  defaultOpen={location.pathname.startsWith("/foundry")}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        className="py-4 px-2 cursor-pointer"
                        tooltip="Foundry"
                      >
                        <Hammer />
                        <span className="text-sm">Foundry</span>
                        <ChevronRight className="!ml-auto transition-transform duration-200 group-data-open/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub className="gap-0">
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="py-4 cursor-pointer"
                            isActive={
                              location.pathname === "/foundry/blogs" ||
                              location.pathname.startsWith("/foundry/blogs/")
                            }
                          >
                            <Link to="/foundry/blogs">
                              <span className="text-sm">Blogs</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="py-4 cursor-pointer"
                            isActive={
                              location.pathname === "/foundry/landing-pages" ||
                              location.pathname.startsWith(
                                "/foundry/landing-pages/"
                              )
                            }
                          >
                            <Link to="/foundry/landing-pages">
                              <span className="text-sm">Landing Pages</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="py-4 cursor-pointer"
                            isActive={
                              location.pathname === "/foundry/tools" ||
                              location.pathname.startsWith("/foundry/tools/")
                            }
                          >
                            <Link to="/foundry/tools">
                              <span className="text-sm">Tools</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="py-4 cursor-pointer"
                            isActive={
                              location.pathname === "/foundry/lead-forms" ||
                              location.pathname.startsWith(
                                "/foundry/lead-forms/"
                              )
                            }
                          >
                            <Link to="/foundry/lead-forms">
                              <span className="text-sm">Lead Forms</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="py-4 cursor-pointer"
                            isActive={location.pathname.startsWith(
                              "/foundry/control-panel"
                            )}
                          >
                            <Link to="/foundry/control-panel/general">
                              <span className="text-sm">Control Panel</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="py-4 px-2 cursor-pointer"
                    tooltip={keywordClustersItem.title}
                    isActive={location.pathname === keywordClustersItem.path}
                    onClick={() => navigate(keywordClustersItem.path)}
                  >
                    <keywordClustersItem.icon />
                    <span className="text-sm">{keywordClustersItem.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="py-4 px-2 cursor-pointer"
                    tooltip="Settings"
                    isActive={location.pathname.startsWith("/settings")}
                  >
                    <Link to="/settings/workspace/general">
                      <Settings2 />
                      <span className="text-sm">Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="shrink-0 border-t">
        <NavUser
          name={user.first_name}
          email={user.email}
          avatar={user.picture ?? ""}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
